import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Alert, IconButton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PaymentsIcon from '@mui/icons-material/Payments';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import { PageHeader } from '../../components/ui/PageHeader';
import { DataTable } from '../../components/common/DataTable';
import { ModuleColumn } from '../../types';
import { biltyAPI, invoiceAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLocale } from '../../contexts/LocaleContext';
import { useToast } from '../../contexts/ToastContext';
import { brandColors, RADIUS, brandCss } from '../../theme/appTheme';
import { useTableColumns } from '../../hooks/useTableColumns';
import { DEFAULT_ROWS_PER_PAGE } from '../../constants/tableDefaults';
import { useSystemTotal } from '../../hooks/useSystemTotal';

const columns: ModuleColumn[] = [
  { field: 'biltyNumber', header: 'Bilty #' },
  { field: 'date', header: 'Date', format: 'date' },
  { field: 'consignorDisplay', header: 'Sender' },
  { field: 'consigneeDisplay', header: 'Receiver' },
  { field: 'totalAmount', header: 'Total', format: 'currency' },
  { field: 'status', header: 'Status' },
];

export const BiltyListPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const { formatCurrency } = useLocale();
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const canCreate = hasPermission('bilty', 'create');
  const canView = hasPermission('bilty', 'view');
  const canEdit = hasPermission('bilty', 'edit');
  const canDelete = hasPermission('bilty', 'delete');
  const canImport = hasPermission('bilty', 'import');
  const canExport = hasPermission('bilty', 'export');
  const canPrint = hasPermission('bilty', 'print') || canView;
  const canCreateInvoice = hasPermission('invoices', 'create');
  const canViewInvoice = hasPermission('invoices', 'view');
  const tableColumns = useTableColumns('bilty', columns);
  const { total: systemTotal, refresh: refreshTotal } = useSystemTotal('bilty');

  const handleCreateInvoice = async (biltyId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const res = await invoiceAPI.createFromBilty(biltyId);
      showToast('Invoice created');
      navigate(`/invoices/${res.data.invoice._id}`);
    } catch (err: unknown) {
      const error = err as { response?: { status?: number; data?: { invoiceId?: string } } };
      if (error.response?.status === 409 && error.response.data?.invoiceId) {
        navigate(`/invoices/${error.response.data.invoiceId}`);
        return;
      }
      setError('Failed to create invoice from bilty');
    }
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await biltyAPI.getAll();
      setRows(res.data);
      refreshTotal();
    } catch {
      setError('Failed to load bilties');
    } finally {
      setLoading(false);
    }
  }, [refreshTotal]);

  useEffect(() => { load(); }, [load]);

  const displayColumns = tableColumns.columns.map((col) => (
    col.format === 'currency'
      ? {
        ...col,
        render: (row: Record<string, unknown>) => formatCurrency(Number(row[col.field] ?? row.freightAmount ?? 0)),
      }
      : col
  ));

  return (
    <Box>
      <PageHeader
        title="Bilty (LR)"
        subtitle={getPageSubtitle('bilty', 'Bilty', { total: systemTotal })}
        icon={<PaymentsIcon />}
        isCore

        action={canCreate ? (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/bilty/new')}
            sx={{ px: 3, fontWeight: 700, borderRadius: `${RADIUS}px`, background: brandCss.gradientPrimary }}>
            Create Bilty
          </Button>
        ) : undefined}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <DataTable
        columns={[
          ...displayColumns,
          {
            field: '_actions',
            header: 'Actions',
            sortable: false,
            render: (row: Record<string, unknown>) => (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="View">
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/bilty/${row._id}`); }}>
                    <VisibilityOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {canEdit && (
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/bilty/${row._id}/edit`); }}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {canPrint && (
                  <Tooltip title="Print">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/bilty/${row._id}?print=1`); }}>
                      <PrintOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {row.invoiceId && canViewInvoice ? (
                  <Tooltip title="View invoice">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/invoices/${row.invoiceId}`); }}>
                      <ReceiptLongIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : canCreateInvoice ? (
                  <Tooltip title="Create invoice">
                    <IconButton size="small" onClick={(e) => handleCreateInvoice(String(row._id), e)}>
                      <ReceiptLongIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </Box>
            ),
          },
        ]}
        rows={rows}
        loading={loading}
        search={search}
        onSearchChange={(v: string) => { setSearch(v); setPage(0); }}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        onRowClick={(row: Record<string, unknown>) => navigate(`/bilty/${row._id}`)}
        onDelete={canDelete ? async (row: Record<string, unknown>) => {
          if (!window.confirm('Delete this bilty?')) return;
          await biltyAPI.remove(String(row._id));
          setSuccess('Bilty deleted');
          load();
        } : undefined}
        onExport={canExport ? async () => {
          const res = await biltyAPI.export();
          const url = URL.createObjectURL(new Blob([res.data]));
          const a = document.createElement('a'); a.href = url; a.download = 'bilty-export.csv'; a.click();
        } : undefined}
        onImport={canImport ? async (records: Record<string, unknown>[]) => {
          const res = await biltyAPI.import(records);
          setSuccess(`Imported ${res.data.results?.created ?? 0} bilty record(s)`);
          load();
        } : undefined}
        canDelete={canDelete}
        canImport={canImport}
        canExport={canExport}
        enableExcelExport={canExport}
        enableColumnFilters
        exportFileName="bilty"
        exportSheetName="Bilty"
        columnPicker={{
          allColumns: tableColumns.allColumns,
          visibleFields: tableColumns.activeFields,
          defaultFields: tableColumns.defaultFields,
          onSave: tableColumns.updateColumns,
          onReset: tableColumns.resetColumns,
          isCustomized: tableColumns.isCustomized,
        }}
        recordLabel="bilties"
      />
    </Box>
  );
};
